import {
  createEffect,
  createSignal,
  JSXElement,
  mergeProps,
  Show,
  untrack
} from 'solid-js';
import PortalCustomizations from '~/portal-customization';
import { ConfirmModalProvider } from '~/components/ui/Modal';
import EnvAppPagesErrorBoundary from '~/components/ErrorBoundaries/EnvAppPagesErrorBoundary.tsx';
import EnvAppPagesApiConnector from '~/api/EnvAppPagesApiConnector.tsx';
import EnvAppDataContextProvider, {
  useEnvAppDataContext
} from '~/pages/ExternalPages/EnvAppPages/contexts/EnvAppDataContext.tsx';
import OrganizationContextProvider from '~/components/AppData/OrganizationContext.tsx';
import SkLoader from '~/components/ui/SkLoader';
import {
  EnvAppPaths,
  getSlug,
  OrgDetailsDataType
} from '~/components/AppRouter/utils.ts';
import { useLocation } from '@solidjs/router';
import {
  allOf,
  anyOf,
  EnvAppPermissions,
  isAuthorized
} from '~/components/Authorization/utils.ts';

type OrganizationContextWrapperPropType = {
  children: JSXElement;
};

const OrganizationContextWrapper = (
  props: OrganizationContextWrapperPropType
): JSXElement => {
  const merged = mergeProps({}, props);
  const envAppData = useEnvAppDataContext();
  const location = useLocation();

  const [orgDetailsType, setOrgDetailsType] = createSignal<OrgDetailsDataType>(
    OrgDetailsDataType.ENV_APP_GENERIC
  );

  createEffect(() => {
    if (location.pathname.includes(EnvAppPaths.LIST_CONNECTIONS)) {
      untrack(() => {
        setOrgDetailsType(OrgDetailsDataType.ENV_SSO_CONNECTIONS);
      });
    } else if (location.pathname.includes(EnvAppPaths.LIST_DIRECTORIES)) {
      untrack(() => {
        setOrgDetailsType(OrgDetailsDataType.ENV_DIRECTORIES);
      });
    }
  });

  return (
    <Show
      when={
        location.pathname !== getSlug.envAppPages(EnvAppPaths.PASSKEYS, true) &&
        isAuthorized(
          allOf(
            anyOf(
              EnvAppPermissions.ORG_SETTINGS_READ,
              EnvAppPermissions.ORG_SETTINGS_MANAGE
            )
          )
        )
      }
      fallback={merged.children}
    >
      <OrganizationContextProvider
        organizationId={envAppData.organizationId}
        type={orgDetailsType()}
      >
        {(isDataLoaded) => (
          <Show
            when={isDataLoaded}
            fallback={<SkLoader />}
          >
            {merged.children}
          </Show>
        )}
      </OrganizationContextProvider>
    </Show>
  );
};

type EnvAppPagesContainerProps = {
  children: JSXElement;
};

export default function EnvAppPagesContainer(
  props: EnvAppPagesContainerProps
): JSXElement {
  const merged = mergeProps({}, props);
  return (
    <PortalCustomizations>
      <EnvAppPagesErrorBoundary>
        <EnvAppPagesApiConnector>
          <ConfirmModalProvider>
            <EnvAppDataContextProvider>
              <OrganizationContextWrapper>
                {merged.children}
              </OrganizationContextWrapper>
            </EnvAppDataContextProvider>
          </ConfirmModalProvider>
        </EnvAppPagesApiConnector>
      </EnvAppPagesErrorBoundary>
    </PortalCustomizations>
  );
}
